import { useState, useMemo } from 'react'
import { useSubDestMaster } from '../hooks/useSubDestMaster'
import { useAuth } from '../contexts/AuthContext'
import SubDestMasterForm from './SubDestMasterForm'
import type { SubDestMasterResponse } from '../types/master'

type SortField = 'name' | 'sort_num' | 'entry_dt' | 'update_dt'
type SortOrder = 'asc' | 'desc'

export default function SubDestMasterList() {
  const { subDestinations, loading, error, refetch, deleteSubDestination } = useSubDestMaster()
  const { user } = useAuth()
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<SubDestMasterResponse | null>(null)
  const [sortField, setSortField] = useState<SortField>('sort_num')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null)

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const filterSubDestinations = (subDestinations: SubDestMasterResponse[]) => {
    return subDestinations.filter(subDest => {
      if (searchTerm && !subDest.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false
      }
      return true
    })
  }

  const sortSubDestinations = (subDestinations: SubDestMasterResponse[]) => {
    return subDestinations.sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })
  }

  const filteredAndSortedSubDestinations = useMemo(() => {
    if (!subDestinations) return []
    const filtered = filterSubDestinations(subDestinations)
    return sortSubDestinations(filtered)
  }, [subDestinations, searchTerm, sortField, sortOrder])

  const handleEdit = (subDestination: SubDestMasterResponse) => {
    setEditingItem(subDestination)
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteSubDestination(id)
      setShowDeleteConfirm(null)
    } catch (error) {
      console.error('Failed to delete sub destination:', error)
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingItem(null)
    refetch()
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted" />
    return sortOrder === 'asc' ? (
      <i className="fas fa-sort-up text-primary" />
    ) : (
      <i className="fas fa-sort-down text-primary" />
    )
  }

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">読み込み中...</span>
        </div>
        <p className="mt-2 text-muted">利用先を読み込んでいます...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-triangle me-2" />
        利用先読み込みエラー: {error}
      </div>
    )
  }

  return (
    <>
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-6">
              <label className="form-label">利用先を検索</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="名前で検索..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label text-muted">
                {subDestinations.length}件中{filteredAndSortedSubDestinations.length}件を表示
              </label>
            </div>
            <div className="col-md-3">
              {user?.role === 'admin' && (
                <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                  <i className="fas fa-plus me-2" />
                  新規追加
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-map-pin me-2" />
            利用先マスター
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0">
              <thead className="table-dark">
                <tr>
                  <th scope="col" className="sortable" onClick={() => handleSort('name')}>
                    名前 {getSortIcon('name')}
                  </th>
                  <th scope="col">説明</th>
                  <th scope="col" className="sortable" onClick={() => handleSort('sort_num')}>
                    並び順 {getSortIcon('sort_num')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('entry_dt')}>
                    作成日時 {getSortIcon('entry_dt')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('update_dt')}>
                    更新日時 {getSortIcon('update_dt')}
                  </th>
                  <th scope="col" className="text-center">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedSubDestinations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      <i className="fas fa-info-circle me-2" />
                      利用先が見つかりません
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedSubDestinations.map(subDest => (
                    <tr key={subDest.id}>
                      <td className="fw-bold">{subDest.name}</td>
                      <td>{subDest.description || <span className="text-muted">-</span>}</td>
                      <td>
                        {subDest.sort_num !== null ? (
                          <span className="badge bg-secondary">{subDest.sort_num}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-nowrap small">{formatDateTime(subDest.entry_dt)}</td>
                      <td className="text-nowrap small">{formatDateTime(subDest.update_dt)}</td>
                      <td className="text-center">
                        {user?.role === 'admin' ? (
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleEdit(subDest)}
                              title="編集"
                            >
                              <i className="fas fa-edit" />
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => setShowDeleteConfirm(subDest.id)}
                              title="削除"
                            >
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleEdit(subDest)}
                            title="表示"
                          >
                            <i className="fas fa-eye" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <SubDestMasterForm
          subDestination={editingItem}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">削除確認</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(null)}
                />
              </div>
              <div className="modal-body">
                この利用先を削除してもよろしいですか？この操作は元に戻せません。
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDelete(showDeleteConfirm)}
                >
                  削除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}